import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, PenLine, ShieldCheck } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { SignaturePad } from "./SignaturePad";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

// Medicys countersignature of a client signed SOW, from the register drawer.
export function SowCountersign({ deal, onDone }) {
  const { user } = useAuth();
  const flow = deal.client_flow || {};
  const bdEmail = (deal.cognito?.bd_email || "").toLowerCase();
  const allowed = user?.role === "admin" || (!!user?.email && user.email.toLowerCase() === bdEmail);
  const [name, setName] = useState(user?.name || "");
  const [title, setTitle] = useState("");
  const [png, setPng] = useState(null);
  const [busy, setBusy] = useState(false);
  const failed = flow.stage === "countersign_failed";

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/sow/${deal.cid}/countersign`, { signature_png: png, name: name.trim(), title: title.trim() || null });
      toast.success("Countersigned. The fully executed copy is being rendered, filed in Cognito and emailed.");
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Countersigning did not go through.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`rounded-lg border p-4 flex flex-col gap-4 ${failed ? "border-rose-200 bg-rose-50/60" : "border-amber-300 bg-amber-50/60"}`} data-testid="sow-countersign">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-[#0B1220] text-[#3EA8DC] flex items-center justify-center shrink-0"><PenLine className="w-4 h-4" /></span>
        <div className="text-sm text-stone-800 leading-relaxed">
          <p className="font-semibold">{failed ? "Countersignature failed, please try again" : "Awaiting Medicys countersignature"}</p>
          <p>
            Signed by {flow.signed_by} ({flow.signer_email}) on {fmt(flow.signed_at)}. Countersigning renders the fully executed PDF with both signatures, files it in Cognito with SoWSigned and emails it to the client.
          </p>
          {failed && flow.error && <p className="mt-1 text-xs text-rose-700" data-testid="sow-countersign-error">{flow.error}</p>}
        </div>
      </div>
      {flow.client_signed_doc_id && (
        <a href={`${API}/api/sow/${deal.cid}/documents/${flow.client_signed_doc_id}/download`} className="self-start inline-flex items-center gap-1.5 text-xs font-medium text-[#1B4F8A] underline underline-offset-2" data-testid="sow-countersign-review-pdf">
          <Download className="w-3.5 h-3.5" /> Review the copy signed by the client (PDF)
        </a>
      )}
      {allowed ? (
        <div className="flex flex-col gap-3" data-testid="sow-countersign-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs text-stone-600">
              Your full name
              <input value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm text-stone-900" data-testid="sow-countersign-name" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-stone-600">
              Title, optional
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Director, Medicys Ltd" className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm text-stone-900" data-testid="sow-countersign-title" />
            </label>
          </div>
          <div className="flex flex-col gap-1 text-xs text-stone-600">
            Draw your signature
            <SignaturePad onChange={setPng} testid="sow-countersign-pad" />
          </div>
          <button type="button" onClick={submit} disabled={busy || !png || !name.trim()} className="self-start inline-flex items-center gap-2 rounded-md bg-[#1B4F8A] hover:bg-[#163f6e] disabled:opacity-50 text-white text-sm font-semibold px-4 py-2.5" data-testid="sow-countersign-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Countersign and execute
          </button>
        </div>
      ) : (
        <p className="text-xs text-stone-600" data-testid="sow-countersign-not-allowed">
          Only the BD owner of this proposal{deal.bd_poc ? ` (${deal.bd_poc})` : ""} or an admin can countersign.
        </p>
      )}
    </section>
  );
}
